import { useMatch, useSearchParams, useNavigate } from "react-router-dom";
import { manifestByOpeningId } from "../../content/manifest.ts";
import { summaryBySlug } from "../../content/lookup.ts";
import { PRACTICES, practiceByMode, practiceSentence } from "../../trainer/practiceCopy.ts";
import { getTrainer, parseTrainMode, type TrainMode } from "../../trainer/session.ts";
import { Dropdown } from "../layout/Dropdown.tsx";
import { useTrainer } from "./useTrainer.ts";

export function PracticeMenu() {
  const navigate = useNavigate();
  const route = useMatch("/train/:courseId");
  const [params, setParams] = useSearchParams();
  const snapshot = useTrainer();
  const course = route ? summaryBySlug(route.params.courseId || "") : null;
  if (!course) return null;
  const mode = snapshot.slug === course.slug ? snapshot.mode : parseTrainMode(params.get("mode"));
  const setMode = (next: TrainMode) => {
    if (next === mode) return;
    const activeSlug = manifestByOpeningId(snapshot.openingId)?.slug || course.slug;
    if (snapshot.mode === "review" && activeSlug !== course.slug && next !== "review") {
      void navigate(`/train/${activeSlug}?mode=${next}`, { replace: true });
      return;
    }
    const updated = new URLSearchParams(params);
    updated.set("mode", next);
    setParams(updated, { replace: true });
    void getTrainer().start({ slug: course.slug, mode: next, mix: next === "review" });
  };

  return (
    <Dropdown
      className="practice-menu"
      menuTitle="Practice"
      label={
        <>
          Practice <span className="practice-current">· {practiceByMode(mode).title}</span>
        </>
      }
      closeOnSelect
    >
      <p className="dropdown-heading">Choose how to practice</p>
      <div className="practice-choices" role="group" aria-label="Practice mode">
        {PRACTICES.map((practice) => (
          <button
            key={practice.mode}
            type="button"
            className="practice-choice"
            aria-label={practice.title}
            aria-describedby={`practice-description-${practice.mode}`}
            aria-pressed={mode === practice.mode}
            disabled={
              (snapshot.inputLocked && !snapshot.sessionEnded) || snapshot.slug !== course.slug
            }
            onClick={() => setMode(practice.mode)}
          >
            <span className="practice-choice-title">
              {practice.title}
              <span aria-hidden="true">{mode === practice.mode ? "✓" : ""}</span>
            </span>
            <span className="practice-choice-sentence" id={`practice-description-${practice.mode}`}>
              {practiceSentence(practice, snapshot.playerSide === "b" ? "black" : "white")}
            </span>
          </button>
        ))}
      </div>
      <button
        className="practice-restart"
        type="button"
        disabled={(snapshot.inputLocked && !snapshot.sessionEnded) || snapshot.slug !== course.slug}
        onClick={() => void getTrainer().start({ slug: course.slug, mode, mix: mode === "review" })}
      >
        Restart this opening
      </button>
      <p className="practice-engine-status" id="coachStatus" role="status">
        {snapshot.engineStatus}
      </p>
    </Dropdown>
  );
}
